import Close from '@mui/icons-material/Close';
import Settings from '@mui/icons-material/Settings';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { DateTimeFormat, getLocalDateTime } from '../../../../../../common/utils/dateFunctions';
import { formatNumberCommaSeparated } from '../../../../../../common/utils/numberFunctions';
import { formatPercentage } from '../../../../../../common/utils/percentageFunctions';
import { uuidv4 } from '../../../../../../common/utils/utilities';
import { ClientSideDataTable } from '../../../../../../common/components/dataTable/clientSide/clientSideDataTable';
import { DatatableColumn } from '../../../../../../common/components/dataTable/types';
import { getDetailRowSx, TABLE_PAPER_ELEVATION, TABLE_PAPER_SX, getTableRowProps } from '../../../../../../common/components/dataTable/tableStyles';
import { LoadingProgress } from '../../../../../../common/store/types';
import { formatDollars } from './../../../../../../common/utils/currencyFunctions';
import {
  AssetGroup,
  AssetGroupItem,
  ExpandedRow,
  GroupingType,
  PortfolioDetailResult,
  PortfolioParameters,
  SecurityColumnVisibility,
  UnrealisedGainsLossesGroup,
} from './../store/types';
import { findMatchedItem, getSecurityTableFields, isUnrealisedGainsLossesGroup } from './SecurityListTableHelper';
import { getSecurityLinkTarget } from './securityLinkHelper';
import { useDispatch, useSelector } from 'react-redux';
import history from '../../../../../../history';
import { transactionsSlice } from '../../../transactions/store/slice';
import { cashAccountsSlice } from '../../../cashAccounts/store/slice';
import { getAccountIdForSecurityCode } from '../../../transactions/store/thunks';
import { selectAccounts } from '../../../common/store/selectors';
import { MUIDataTableViewCol, TableViewCol } from 'mui-datatables';
import WO2Button from 'src/common/components/button/Button';
import { ColumnChanges } from 'src/store/userSettings/preferences/portfolio/types';
import { getColumnLabel } from 'src/store/userSettings/preferences/portfolio/columnChangeDetection';

export interface Props {
  progress: LoadingProgress;
  data: AssetGroupItem[];
  parameters: PortfolioParameters;
  portfolioDetailResult?: PortfolioDetailResult;
  assetClassTitle: string;
  selectedAssetGroup: AssetGroup | null;
  columnVisibility: SecurityColumnVisibility;
  columnChanges?: ColumnChanges;
  setParameters: (parameters: PortfolioParameters) => void;
  setColumnVisibility: (visibility: SecurityColumnVisibility) => void;
  onSaveDefaultColumns: () => void;
  onResetDefaultColumns: () => void;
}

export function SecurityListTable({
  portfolioDetailResult,
  progress,
  parameters,
  columnVisibility,
  columnChanges,
  setColumnVisibility,
  onSaveDefaultColumns,
  onResetDefaultColumns,
  setParameters,
}: Props): JSX.Element {
  const [expanded, setExpanded] = useState<ExpandedRow>([]);
  const [rowsExpanded, setRowsExpanded] = useState<number[]>([]);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [resetSnackbarOpen, setResetSnackbarOpen] = useState(false);
  const dispatch = useDispatch();
  const clientAccounts = useSelector(selectAccounts);
  const clientId = portfolioDetailResult?.clientId;

  const handleSecurityClick = async (row: AssetGroupItem) => {
    const target = getSecurityLinkTarget(row);
    if (target === null || !clientId || !row.securityCode) {
      return;
    }

    if (target === 'transactions') {
      dispatch(transactionsSlice.actions.setSecurityCodeFilterParameter(row.securityCode));
      history.push(`/client/${clientId}/transactions`);
      return;
    }

    try {
      const accountIds = clientAccounts.map((account) => account.accountId);
      const accountId = await getAccountIdForSecurityCode(clientId, accountIds, row.securityCode);
      if (accountId != null) {
        dispatch(
          cashAccountsSlice.actions.setAccountParameter({
            accountId,
            accountName: '',
            accountNumber: '',
            accountIdList: [accountId],
          })
        );
        dispatch(cashAccountsSlice.actions.setSecurityParameter(row.securityId));
      }
    } finally {
      history.push(`/client/${clientId}/cashaccount`);
    }
  };

  const rows: AssetGroup[] = useMemo(() => {
    return portfolioDetailResult?.portfolioDetail?.items ?? [];
  }, [portfolioDetailResult?.portfolioDetail?.items]);

  const columns: DatatableColumn[] = useMemo(
    () => [
      {
        name: 'name',
        label: 'NAME',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['name'],
          viewColumns: false,
          setCellProps: () => ({
            style: {
              maxWidth: '350px',
              width: '350px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }),
          setCellHeaderProps: () => ({
            style: {
              maxWidth: '350px',
              width: '350px',
            },
          }),
          customBodyRenderLite(dataIndex: number) {
            const row = rows[dataIndex];
            const groupName = row?.groupName ?? '';
            const groupCode = row?.securityCode ?? '';
            const groupNameWithCode = parameters?.groupingType === GroupingType.Account && groupCode ? `${groupName} | ${groupCode}` : groupName;
            return (
              <Tooltip title={groupNameWithCode}>
                <Typography fontWeight="bold">
                  {groupNameWithCode.length > 70 ? `${groupNameWithCode.substring(0, 70)}...` : groupNameWithCode ?? ''}
                </Typography>
              </Tooltip>
            );
          },
        },
      },
      {
        name: 'securityCode',
        label: 'CODE',
        textAlign: 'left',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['securityCode'],
          viewColumns: true,
          customBodyRenderLite: (): React.ReactNode => null,
          setCellProps: () => ({
            style: {
              maxWidth: '100px',
              width: '100px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }),
          setCellHeaderProps: () => ({
            style: {
              maxWidth: '100px',
              width: '100px',
            },
          }),
        },
      },
      {
        name: 'units',
        label: 'UNITS',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['units'],
          customBodyRenderLite: (): React.ReactNode => null,
        },
      },
      {
        name: 'costBase',
        label: 'COST BASE',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['costBase'],
          customBodyRenderLite: (index: number): React.ReactNode => {
            const row = rows[index];
            const fields = getSecurityTableFields(row, portfolioDetailResult);
            return <Typography fontWeight="bold">{formatDollars(fields?.costBase ?? 0)}</Typography>;
          },
        },
      },
      {
        name: 'unitCost',
        label: 'AVG UNIT COST',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['unitCost'],
          customBodyRenderLite: (): React.ReactNode => null,
        },
      },
      {
        name: 'unitPrice',
        label: 'PRICE (AUD)',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['unitPrice'],
          customBodyRenderLite: (): React.ReactNode => null,
        },
      },
      {
        name: 'nativePrice',
        label: 'PRICE (NATIVE)',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['nativePrice'],
          viewColumns: true,
          customBodyRenderLite: (): React.ReactNode => null,
        },
      },
      {
        name: 'currentValue',
        label: 'VALUE (AUD)',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['currentValue'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{formatDollars(rows[dataIndex]?.currentValue ?? 0)}</Typography>
          ),
        },
      },
      {
        name: 'nativeValue',
        label: 'VALUE (NATIVE)',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['nativeValue'],
          viewColumns: true,
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{formatDollars(rows[dataIndex]?.nativeValue ?? 0)}</Typography>
          ),
        },
      },
      {
        name: 'currentWeightPercentage',
        label: 'PORT.%',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['currentWeightPercentage'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{formatPercentage(rows[dataIndex]?.currentWeightPercentage ?? 0)}</Typography>
          ),
        },
      },
      {
        name: 'gainLossValue',
        label: 'UNREALISED GAIN/LOSS',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['gainLossValue'],
          customBodyRenderLite: (index: number): React.ReactNode => {
            const row = rows[index];
            const fields = getSecurityTableFields(row, portfolioDetailResult);
            return <Typography fontWeight="bold">{formatDollars(fields?.gainLossValue ?? 0)}</Typography>;
          },
        },
      },
      {
        name: 'gainLossPercentage',
        label: 'UNREALISED GAIN/LOSS %',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['gainLossPercentage'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => {
            const row = rows[dataIndex];
            const fields = getSecurityTableFields(row, portfolioDetailResult);
            return <Typography fontWeight="bold">{formatPercentage(fields?.gainLossPercentage ?? 0)}</Typography>;
          },
        },
      },
      {
        name: 'returnSinceInception',
        label: 'RETURN SINCE INCEPTION',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['returnSinceInception'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => {
            const row = rows[dataIndex];
            const sum = row?.items?.reduce((acc, item) => acc + (item.returnSinceInception ?? 0), 0) ?? row?.returnSinceInception ?? 0;
            return <Typography fontWeight="bold">{formatDollars(sum)}</Typography>;
          },
        },
      },
      {
        name: 'returnSinceInceptionPercentage',
        label: 'RETURN SINCE INCEPTION %',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['returnSinceInceptionPercentage'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => {
            const percent = rows[dataIndex]?.returnSinceInceptionPercentage;
            return <Typography fontWeight="bold">{percent ? formatPercentage(percent) : '-'}</Typography>;
          },
        },
      },
      {
        name: 'estimatedIncome',
        label: 'EST.INCOME',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['estimatedIncome'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{formatDollars(rows[dataIndex]?.estimatedIncome ?? 0)}</Typography>
          ),
        },
      },
      {
        name: 'estimatedYield',
        label: 'EST. YIELD',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['estimatedYield'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{formatPercentage(rows[dataIndex]?.estimatedYield ?? 0)}</Typography>
          ),
        },
      },
      {
        name: 'estimatedGrossIncome',
        label: 'EST.GROSS INCOME',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['estimatedGrossIncome'],
          viewColumns: true,
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{formatDollars(rows[dataIndex]?.estimatedGrossIncome ?? 0)}</Typography>
          ),
        },
      },
      {
        name: 'estimatedGrossYield',
        label: 'EST.GROSS YIELD',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['estimatedGrossYield'],
          viewColumns: true,
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{formatPercentage(rows[dataIndex]?.estimatedGrossYield ?? 0)}</Typography>
          ),
        },
      },
      {
        name: 'priceDate',
        label: 'PRICE DATE',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['priceDate'],
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{getLocalDateTime(rows[dataIndex]?.priceDate || undefined, DateTimeFormat.Short)}</Typography>
          ),
        },
      },
      {
        name: 'currencyCode',
        label: 'NATIVE CURRENCY',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['currencyCode'],
          viewColumns: true,
          customBodyRenderLite: (dataIndex: number): React.ReactNode => (
            <Typography fontWeight="bold">{rows[dataIndex]?.currencyCode ?? ''}</Typography>
          ),
        },
      },
      {
        name: 'forexRate',
        label: 'FOREX RATE',
        textAlign: 'right',
        options: {
          filter: true,
          sort: true,
          display: columnVisibility['forexRate'],
          viewColumns: true,
          customBodyRenderLite: (dataIndex: number): React.ReactNode => <Typography fontWeight="bold">{rows[dataIndex]?.forexRate ?? ''}</Typography>,
        },
      },
    ],
    [columnVisibility, rows, portfolioDetailResult?.gainsLossesDetail]
  );

  useEffect(() => {
    const allExpanded: ExpandedRow = {};
    const allRowsExpanded: number[] = [];

    rows.forEach((row, parentIndex) => {
      allRowsExpanded.push(parentIndex);

      if (row && row.items && row.items.length > 0) {
        allExpanded[parentIndex] = row.items.map((_, index) => index);
      }
    });

    setExpanded(allExpanded);
    setRowsExpanded(allRowsExpanded);
  }, [portfolioDetailResult]);

  const details = (group: UnrealisedGainsLossesGroup | undefined, row: AssetGroupItem, parentIndex: number, key: number, visible: boolean) => {
    const matched = findMatchedItem(row, group);
    const isLinkable = getSecurityLinkTarget(row) !== null;
    const securityNameDisplay = row.securityName && row.securityName.length > 70 ? `${row.securityName.substring(0, 70)}...` : row.securityName;

    return (
      <TableRow
        key={uuidv4()}
        style={{
          visibility: (expanded[parentIndex] && expanded[parentIndex].includes(key)) || visible ? 'visible' : 'collapse',
        }}
        sx={getDetailRowSx(key)}
      >
        <TableCell />
        <TableCell>
          <Tooltip title={row.securityName || ''}>
            {isLinkable ? (
              <Link
                component="button"
                type="button"
                color="primary"
                underline="hover"
                onClick={() => handleSecurityClick(row)}
                sx={{ cursor: 'pointer', textAlign: 'left' }}
              >
                {securityNameDisplay}
              </Link>
            ) : (
              <Typography>{securityNameDisplay}</Typography>
            )}
          </Tooltip>
        </TableCell>
        {columnVisibility['securityCode'] && (
          <TableCell align="left">
            {isLinkable ? (
              <Link
                component="button"
                type="button"
                color="primary"
                underline="hover"
                onClick={() => handleSecurityClick(row)}
                sx={{ cursor: 'pointer' }}
              >
                {row.securityCode}
              </Link>
            ) : (
              <Typography>{row.securityCode}</Typography>
            )}
          </TableCell>
        )}
        {columnVisibility['units'] && (
          <TableCell align="right">
            <Typography>{formatNumberCommaSeparated(row.units, parameters.unitDecimalPlaces)}</Typography>
          </TableCell>
        )}
        {columnVisibility['costBase'] && (
          <TableCell align="right">
            <Typography>{formatDollars(matched?.costBase ?? 0)}</Typography>
          </TableCell>
        )}
        {columnVisibility['unitCost'] && (
          <TableCell align="right">
            <Typography>{formatDollars(matched?.unitCost ?? 0, false, 4)}</Typography>
          </TableCell>
        )}
        {columnVisibility['unitPrice'] && (
          <TableCell align="right">
            <Typography>{formatDollars(row.unitPrice, false, 4)}</Typography>
          </TableCell>
        )}
        {columnVisibility['nativePrice'] && (
          <TableCell align="right">
            <Typography>{formatDollars(row.nativePrice, false, 4)}</Typography>
          </TableCell>
        )}
        {columnVisibility['currentValue'] && (
          <TableCell align="right">
            <Typography>{formatDollars(row.currentValue)}</Typography>
          </TableCell>
        )}
        {columnVisibility['nativeValue'] && (
          <TableCell align="right">
            <Typography>{formatDollars(row.nativeValue)}</Typography>
          </TableCell>
        )}
        {columnVisibility['currentWeightPercentage'] && (
          <TableCell align="right">
            <Typography>{formatPercentage(row.currentWeightPercentage)}</Typography>
          </TableCell>
        )}
        {columnVisibility['gainLossValue'] && (
          <TableCell align="right">
            <Typography>{formatDollars(matched?.gainLossValue ?? 0)}</Typography>
          </TableCell>
        )}
        {columnVisibility['gainLossPercentage'] && (
          <TableCell align="right">
            <Typography>{formatPercentage(matched?.gainLossPercentage ?? 0)}</Typography>
          </TableCell>
        )}
        {columnVisibility['returnSinceInception'] && (
          <TableCell align="right">
            <Typography>{formatDollars(row.returnSinceInception ?? 0)}</Typography>
          </TableCell>
        )}
        {columnVisibility['returnSinceInceptionPercentage'] && (
          <TableCell align="right">
            <Typography>{row.returnSinceInceptionPercentage ? formatPercentage(row.returnSinceInceptionPercentage) : '-'}</Typography>
          </TableCell>
        )}
        {columnVisibility['estimatedIncome'] && (
          <TableCell align="right">
            <Typography>{formatDollars(row.estimatedIncome)}</Typography>
          </TableCell>
        )}
        {columnVisibility['estimatedYield'] && (
          <TableCell align="right">
            <Typography>{formatPercentage(row.estimatedYield)}</Typography>
          </TableCell>
        )}
        {columnVisibility['estimatedGrossIncome'] && (
          <TableCell align="right">
            <Typography>{formatDollars(row.estimatedGrossIncome)}</Typography>
          </TableCell>
        )}
        {columnVisibility['estimatedGrossYield'] && (
          <TableCell align="right">
            <Typography>{formatPercentage(row.estimatedGrossYield)}</Typography>
          </TableCell>
        )}
        {columnVisibility['priceDate'] && (
          <TableCell align="right">
            <Typography>{getLocalDateTime(row.priceDate, DateTimeFormat.Short)}</Typography>
          </TableCell>
        )}
        {columnVisibility['currencyCode'] && (
          <TableCell align="right">
            <Typography>{row.currencyCode}</Typography>
          </TableCell>
        )}
        {columnVisibility['forexRate'] && (
          <TableCell align="right">
            <Typography>{formatNumberCommaSeparated(row.forexRate, 6)}</Typography>
          </TableCell>
        )}
      </TableRow>
    );
  };

  const detailedColumn = (rowMeta: { dataIndex: number; rowIndex: number }): React.ReactNode => {
    const parentResult = getSecurityTableFields(rows[rowMeta.dataIndex], portfolioDetailResult);
    const parentGroup = isUnrealisedGainsLossesGroup(parentResult) ? parentResult : undefined;

    return (
      <>
        {rows[rowMeta.dataIndex]?.items.map((item: AssetGroupItem, index: number) => {
          return details(parentGroup, item, rowMeta.dataIndex, index, true);
        })}
      </>
    );
  };

  const closeColumnVisibilityMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    const popoverPaper = (event.currentTarget as HTMLElement).closest('.MuiPopover-paper');
    const closeBtn = popoverPaper?.querySelector<HTMLButtonElement>('[aria-label="Close"]');
    closeBtn?.click();
  };

  const saveView = (event: React.MouseEvent<HTMLButtonElement>) => {
    onSaveDefaultColumns();
    closeColumnVisibilityMenu(event);
  };

  const resetView = (event: React.MouseEvent<HTMLButtonElement>) => {
    onResetDefaultColumns();
    setResetSnackbarOpen(true);
    closeColumnVisibilityMenu(event);
  };

  return (
    <>
      <Paper elevation={TABLE_PAPER_ELEVATION} sx={TABLE_PAPER_SX}>
        <ClientSideDataTable
          loadingProgress={progress}
          columns={columns}
          data={rows ?? []}
          title={
            <Typography sx={{ fontSize: '20px' }}>
              Portfolio Value: <span style={{ fontWeight: 700 }}>{formatDollars(portfolioDetailResult?.portfolioDetail?.marketValue ?? 0)}</span>
            </Typography>
          }
          components={{
            TableViewCol: (props: MUIDataTableViewCol) => (
              <>
                {columnChanges && columnChanges.hasChanges && (
                  <Alert severity="info" variant="outlined" sx={{ m: 1 }}>
                    {columnChanges.newColumns.length > 0 && (
                      <Typography variant="body2">
                        <strong>Added:</strong> {columnChanges.newColumns.map((col) => getColumnLabel(col)).join(', ')}
                      </Typography>
                    )}
                    {columnChanges.removedColumns.length > 0 && (
                      <Typography variant="body2">
                        <strong>Removed:</strong> {columnChanges.removedColumns.map((col) => getColumnLabel(col)).join(', ')}
                      </Typography>
                    )}
                  </Alert>
                )}
                <TableViewCol {...props} />
                <Divider />
                <Box display="flex" flexDirection="column" gap={1} p={1}>
                  <WO2Button onClick={saveView} fullWidth variant="contained" color="primary">
                    Save view
                  </WO2Button>
                  <WO2Button onClick={resetView} fullWidth variant="outlined" color="primary">
                    Reset
                  </WO2Button>
                </Box>
              </>
            ),
          }}
          options={{
            filter: false,
            viewColumns: true,
            expandableRows: true,
            expandableRowsOnClick: true,
            rowsExpanded: rowsExpanded,
            customToolbar: () => {
              if (progress.isLoading) {
                return null;
              }
              return (
                <Tooltip title="Column Settings">
                  <IconButton
                    onClick={(e) => {
                      setSettingsAnchor(e.currentTarget);
                    }}
                    size="small"
                  >
                    <Settings />
                  </IconButton>
                </Tooltip>
              );
            },
            onViewColumnsChange: (changedColumn: string, action: string) => {
              const currentColumns: SecurityColumnVisibility = {
                ...columnVisibility,
              };
              const visible = action === 'add';
              currentColumns[changedColumn as keyof SecurityColumnVisibility] = visible;
              setColumnVisibility(currentColumns);
            },
            setRowProps: getTableRowProps,
            renderExpandableRow: (_rowData, rowMeta) => detailedColumn(rowMeta),
            customTableBodyFooterRender: () => {
              return (
                <TableFooter>
                  <TableRow>
                    <TableCell />
                    <TableCell>
                      <Typography color="black" fontWeight={700}>
                        Grand Total
                      </Typography>
                    </TableCell>
                    {columnVisibility['securityCode'] && <TableCell align="left" />}
                    {columnVisibility['units'] && <TableCell align="right" />}
                    {columnVisibility['costBase'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatDollars(portfolioDetailResult?.gainsLossesDetail?.totalCostBase ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['unitCost'] && <TableCell align="right"></TableCell>}
                    {columnVisibility['unitPrice'] && <TableCell align="right"></TableCell>}
                    {columnVisibility['nativePrice'] && <TableCell align="right"></TableCell>}
                    {columnVisibility['currentValue'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatDollars(portfolioDetailResult?.portfolioDetail?.marketValue ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['nativeValue'] && <TableCell align="right"></TableCell>}
                    {columnVisibility['currentWeightPercentage'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          100%
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['gainLossValue'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatDollars(portfolioDetailResult?.gainsLossesDetail?.totalUnrealisedGainLoss ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['gainLossPercentage'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatPercentage(portfolioDetailResult?.gainsLossesDetail?.gainLossPercentage ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['returnSinceInception'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatDollars(
                            rows.reduce(
                              (total, group) =>
                                total +
                                (group.items?.reduce((acc, item) => acc + (item.returnSinceInception ?? 0), 0) ?? group.returnSinceInception ?? 0),
                              0
                            )
                          )}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['returnSinceInceptionPercentage'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {portfolioDetailResult?.portfolioDetail?.totalReturnSinceInceptionPercentage
                            ? formatPercentage(portfolioDetailResult.portfolioDetail.totalReturnSinceInceptionPercentage)
                            : '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['estimatedIncome'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatDollars(portfolioDetailResult?.portfolioDetail?.totalEstimatedIncome ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['estimatedYield'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatPercentage(portfolioDetailResult?.portfolioDetail?.totalEstimatedYield ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['estimatedGrossIncome'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatDollars(portfolioDetailResult?.portfolioDetail?.totalEstimatedGrossIncome ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['estimatedGrossYield'] && (
                      <TableCell align="right">
                        <Typography color="black" fontWeight={700}>
                          {formatPercentage(portfolioDetailResult?.portfolioDetail?.totalEstimatedGrossYield ?? 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility['priceDate'] && <TableCell />}
                    {columnVisibility['currencyCode'] && <TableCell />}
                    {columnVisibility['forexRate'] && <TableCell />}
                  </TableRow>
                </TableFooter>
              );
            },
          }}
          id="client-security-list-datatable"
        />
      </Paper>
      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => {
          setSettingsAnchor(null);
        }}
      >
        <Box display="flex" flexDirection="column" gap={2} paddingTop={1} paddingBottom={1} paddingLeft={2} paddingRight={2}>
          <Box display="flex" gap={2} alignItems="center">
            <Typography sx={{ color: '#A6A6A6' }} fontSize={12} variant="caption" flex={1}>
              Column Settings
            </Typography>
            <IconButton size="small" onClick={() => setSettingsAnchor(null)}>
              <Close />
            </IconButton>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Typography sx={{ textTransform: 'uppercase' }}>Units</Typography>
            <TextField
              fullWidth
              label="Decimals"
              type="number"
              inputProps={{ min: 0, max: 8 }}
              onChange={(e) => {
                const value = e.target.value;
                const numValue = value === '' ? 0 : parseInt(value, 10);
                if (numValue >= 0 && numValue <= 8) {
                  setParameters({ ...parameters, unitDecimalPlaces: numValue });
                }
              }}
              onFocus={(e) => e.target.select()}
              value={parameters.unitDecimalPlaces.toString()}
            />
          </Box>
        </Box>
      </Menu>
      <Snackbar open={resetSnackbarOpen} autoHideDuration={3000} onClose={() => setResetSnackbarOpen(false)}>
        <Alert onClose={() => setResetSnackbarOpen(false)} severity="success" variant="filled">
          Columns reset to default
        </Alert>
      </Snackbar>
    </>
  );
}
